import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, HeaderText, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import SurveyStartModal from 'modules/surveys/components/Start/SurveyStartModal'
import type SurveyForm from 'modules/surveys/store/models/Form'

type ModalProps = React.ComponentProps<typeof Modal>

interface Props {
  editCopy: string
  form: SurveyForm
  startCopy: string
  startRoute: string
  reactivateCopy: string
  hasAtLeastOneQuestion: boolean
  canEditSurveyForm: boolean
}
interface ModalActionsProps {
  form: SurveyForm
}

function ModalActions({ form }: ModalActionsProps) {
  const { closePortal } = usePortal()

  const { performAction } = useAsyncAction(
    async () => {
      const { success } = await asyncAction(() => form.remindRespondents(), {
        successFlash: false,
        errorFlash: false,
      })

      if (success) {
        closePortal()
      }
    },
    {
      successFlash: false,
      errorFlash: false,
    }
  )

  const title = i18n.t('surveys.form.configuration.stats.title')

  return (
    <Box flexDirection="row" gap="s8">
      <Button type="secondary" onClick={() => closePortal()}>
        {i18n.t('common.close')}
      </Button>
      <Button onClick={performAction}>{title}</Button>
    </Box>
  )
}

interface ConfirmModalProps {
  onClose: ModalProps['onClose']
  form: SurveyForm
}

function ConfirmModal({ onClose, form }: ConfirmModalProps) {
  const title = i18n.t('surveys.form.configuration.stats.title')
  const body = i18n.t('surveys.form.configuration.stats.body')

  return (
    <Modal
      illustration="send-email-envelope"
      title={title}
      description={body}
      footer={<ModalActions form={form} />}
      onClose={onClose}
    />
  )
}

interface ActionProps {
  form: SurveyForm
}

function Actions({ form }: ActionProps) {
  const [isOpen, toggleOpen] = useToggle(false)
  const [modalIsOpen, toggleModal] = useToggle(false)

  const title = i18n.t('surveys.form.configuration.stats.title')
  const description = i18n.t('surveys.form.configuration.stats.description')

  const options = [
    {
      title: title,
      description: description,
      onClick: toggleModal(true),
    },
  ]

  return (
    <>
      {!form.isFinished && (
        <Popover attachment="top center" open={isOpen} toggle={toggleOpen}>
          <Button type="secondary" onClick={toggleOpen(true)} iconName="ellipsis" />
          <PopoverContent onClose={toggleOpen(false)} options={options} />
        </Popover>
      )}
      {modalIsOpen && <ConfirmModal form={form} onClose={toggleModal(false)} />}
    </>
  )
}

function Header({
  editCopy,
  form,
  startCopy,
  startRoute,
  reactivateCopy,
  hasAtLeastOneQuestion,
  canEditSurveyForm,
}: Props) {
  const [isOpen, toggleOpen] = useToggle(false)
  const navigate = useNavigate()
  const disabled = !canEditSurveyForm || !hasAtLeastOneQuestion
  const startQuestionaire = () => {
    navigate(startRoute)
  }

  return (
    <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
      <Box flexDirection="row" alignItems="center" gap="s16">
        <HeaderText text={form.attributes.name} />
      </Box>
      {form.isDraft && (
        <Box>
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => startQuestionaire()}
            aria-label={startCopy}
          >
            {startCopy}
          </Button>
        </Box>
      )}
      {(form.isActive || form.isFinished) && (
        <Box flexDirection="row" gap="s16">
          <Button disabled={!canEditSurveyForm} type="secondary" onClick={toggleOpen(true)}>
            {form.isActive ? editCopy : reactivateCopy}
          </Button>
          {isOpen && <SurveyStartModal onClose={toggleOpen(false)} form={form} edit />}
          <Actions form={form} />
        </Box>
      )}
    </Box>
  )
}

export default observer(Header)
